import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Link, Paper, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2Outlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import ENV from "../../../config/env";
import { useAuth } from "../../../context/AuthContext";
import ComprobanteDialog from "../../egresos/components/ComprobanteDialog";
import OrdenFormDialog from "../components/OrdenFormDialog";
import LineaFormDialog from "../components/LineaFormDialog";
import AduanaFormDialog from "../components/AduanaFormDialog";
import EnlazarPagoDialog from "../components/EnlazarPagoDialog";
import EquiposLineaDialog from "../components/EquiposLineaDialog";
import {
  eliminarArchivoOC, eliminarLinea, finalizarOrden, fmt, getCatalogosOC, getOrden, mensajeError, monedaTexto, simbolo, subirArchivosOC,
} from "../services/ordenCompraService";

const headSx = { fontWeight: 800, whiteSpace: "nowrap", fontSize: 11.5, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3 };
const cellSx = { py: 0.75, px: 1.25, fontSize: 12.5, verticalAlign: "middle" };

const Tarjeta = ({ titulo, acciones, children, sx }) => (
  <Paper variant="outlined" sx={{ p: 2, display: "grid", gap: 1.25, alignContent: "start", ...sx }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{titulo}</Typography>
      {acciones}
    </Box>
    {children}
  </Paper>
);

const Dato = ({ etiqueta, valor }) => (
  <Box sx={{ display: "flex", gap: 1.5, py: 0.4, borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
    <Typography sx={{ fontSize: 12.5, color: "text.secondary", width: 120, flexShrink: 0 }}>{etiqueta}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, wordBreak: "break-word" }}>{valor || "—"}</Typography>
  </Box>
);

// Tarjeta de resumen de la parte superior (total, pagado, crédito fiscal, equipos recibidos).
const Indicador = ({ titulo, valor, detalle, progreso, color = "primary" }) => (
  <Paper variant="outlined" sx={{ p: 1.5, display: "grid", gap: 0.4, alignContent: "start" }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>{titulo}</Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.15, fontVariantNumeric: "tabular-nums" }}>{valor}</Typography>
    <Typography sx={{ fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums", minHeight: 16 }}>{detalle}</Typography>
    {progreso !== undefined && <LinearProgress variant="determinate" value={Math.min(Math.max(progreso, 0), 100)} color={color} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />}
  </Paper>
);

// Detalle de una orden de compra: datos, embarcador, aduana, líneas de compra con su costo unitario real y los pagos (egresos) enlazados.
export default function OrdenCompraDetallePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const puedeVerEgreso = ["admin", "gerente", "conta"].includes(user?.role);
  const [datos, setDatos] = useState(null);
  const [catalogos, setCatalogos] = useState(null);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState({ texto: "", severidad: "success" });
  const [editar, setEditar] = useState(false);
  const [linea, setLinea] = useState({ abierto: false, linea: null });
  const [aduana, setAduana] = useState({ abierto: false, aduana: null });
  const [enlazar, setEnlazar] = useState(false);
  const [equiposLinea, setEquiposLinea] = useState(null); // id de la línea cuyos equipos se ven
  const [confirmar, setConfirmar] = useState(null); // { titulo, texto, accion, boton, color }
  const [procesando, setProcesando] = useState(false);
  const [egresoVer, setEgresoVer] = useState(null);
  const inputArchivos = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await getOrden(id);
      setDatos(data);
      setError("");
    } catch (e) {
      setError(mensajeError(e, "No se pudo cargar la orden de compra."));
    }
  }, [id]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { getCatalogosOC().then(({ data }) => setCatalogos(data)).catch(() => setCatalogos(null)); }, []);

  const ok = (texto) => setAviso({ texto, severidad: "success" });
  const falla = (texto) => setAviso({ texto, severidad: "error" });

  const ejecutar = async () => {
    setProcesando(true);
    try {
      await confirmar.accion();
      ok(confirmar.exito);
      setConfirmar(null);
      await cargar();
    } catch (e) {
      falla(mensajeError(e, "No se pudo completar la acción."));
      setConfirmar(null);
    } finally {
      setProcesando(false);
    }
  };

  const subir = async (e) => {
    const archivos = Array.from(e.target.files);
    e.target.value = "";
    if (!archivos.length) return;
    try { await subirArchivosOC(id, archivos); ok("Archivos subidos."); await cargar(); } catch (err) { falla(mensajeError(err, "No se pudieron subir los archivos.")); }
  };

  if (!datos) {
    return (
      <DashboardLayout>
        <PageHeader title={`Orden de compra N.º ${id}`} backTo="/ordenes-compra" />
        <Box sx={{ p: 3 }}>{error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={26} />}</Box>
      </DashboardLayout>
    );
  }

  const { orden, editable, lineas, aduanas, pagos, archivos, totales } = datos;
  const sim = simbolo(orden.moneda);
  const emitidos = pagos.filter((p) => p.emitido);
  const pendientes = pagos.filter((p) => !p.emitido && !p.solicitud_anulada);
  const anulados = pagos.filter((p) => p.solicitud_anulada); // pedidos cuya solicitud de cheque se anuló
  const conSerie = lineas.filter((l) => l.equipos === "serie");
  const equiposEsperados = conSerie.reduce((a, l) => a + l.cantidad_entera, 0);
  const equiposRecibidos = conSerie.reduce((a, l) => a + Math.min(l.equipos_registrados, l.cantidad_entera), 0);
  const pctPagado = totales.bolivianos > 0 ? (totales.pagado_bs / totales.bolivianos) * 100 : 0;

  return (
    <DashboardLayout>
      <PageHeader
        title={`Orden de compra N.º ${orden.id}`}
        subtitle={`${orden.proveedor}${orden.cliente ? ` · para ${orden.cliente}` : ""}`}
        backTo="/ordenes-compra"
        actions={(
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Chip size="small" label={orden.estado === "Proceso" ? "En proceso" : "Finalizada"} color={orden.estado === "Proceso" ? "success" : "default"} sx={{ fontWeight: 700 }} />
            {editable && <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditar(true)} sx={{ textTransform: "none" }}>Modificar</Button>}
            {editable && (
              <Button
                size="small" variant="contained" startIcon={<CheckCircleIcon />} sx={{ textTransform: "none", fontWeight: 700 }}
                onClick={() => setConfirmar({ titulo: "Finalizar orden de compra", texto: "Al finalizar ya no se podrán modificar los datos, las líneas, la aduana ni los pagos de esta orden. ¿Finalizarla?", accion: () => finalizarOrden(orden.id), exito: "Orden finalizada.", boton: "Sí, finalizar" })}
              >Finalizar orden</Button>
            )}
          </Box>
        )}
      />

      <Box sx={{ p: { xs: 1.5, md: 2 }, display: "grid", gap: 1.5 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" } }}>
          <Indicador titulo="Total de la compra" valor={`${sim} ${fmt(totales.compra)}`} detalle={`Bs ${fmt(totales.bolivianos)} · $us ${fmt(totales.dolares)}`} />
          <Indicador
            titulo="Pagado" valor={`Bs ${fmt(totales.pagado_bs)}`} detalle={emitidos.length ? `$us ${fmt(totales.pagado_usd)} · ${emitidos.length} egreso${emitidos.length === 1 ? "" : "s"}` : "Sin pagos emitidos"}
            progreso={emitidos.length ? pctPagado : 0} color={pctPagado >= 99.5 ? "success" : "primary"}
          />
          <Indicador titulo="Crédito fiscal" valor={`Bs ${fmt(totales.credito_fiscal_bs)}`} detalle={aduanas.length ? `$us ${fmt(totales.credito_fiscal_usd)} · ${aduanas.length} aduana${aduanas.length === 1 ? "" : "s"}` : "Sin aduana registrada"} />
          <Indicador
            titulo="Equipos recibidos" valor={equiposEsperados ? `${equiposRecibidos} de ${equiposEsperados}` : "—"} detalle={equiposEsperados ? "Líneas con registro por serie" : "No hay líneas con serie"}
            progreso={equiposEsperados ? (equiposRecibidos / equiposEsperados) * 100 : 0} color={equiposEsperados && equiposRecibidos >= equiposEsperados ? "success" : "primary"}
          />
        </Box>

        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", lg: "1.5fr 1fr" }, alignItems: "start" }}>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Tarjeta titulo="Datos de la orden">
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 4 }}>
                <Box>
                  <Dato etiqueta="Fecha" valor={orden.fecha} />
                  <Dato etiqueta="Moneda" valor={`${monedaTexto(orden.moneda)} · T.C. ${orden.tipo_cambio}`} />
                  <Dato etiqueta="Proveedor" valor={orden.proveedor} />
                  <Dato etiqueta="Cliente" valor={orden.cliente} />
                </Box>
                <Box>
                  <Dato etiqueta="Embarcador" valor={orden.embarcado} />
                  <Dato etiqueta="Costo de embarque" valor={orden.costo_embarque ? `${sim} ${orden.costo_embarque}` : ""} />
                  <Dato etiqueta="N.º de guía" valor={orden.no_guia} />
                  <Dato etiqueta="Cuotas" valor={orden.cuotas} />
                </Box>
              </Box>
              <Box sx={{ borderTop: 1, borderColor: "divider", pt: 0.5 }}>
                <Dato etiqueta="Observación" valor={orden.observacion} />
                <Dato etiqueta="Registrada por" valor={orden.usu} />
              </Box>

            </Tarjeta>
            <Tarjeta
              titulo={`Proformas y adjuntos (${archivos.length})`}
              acciones={editable && (
                <>
                  <input ref={inputArchivos} type="file" multiple style={{ display: "none" }} onChange={subir} />
                  <Button size="small" startIcon={<AttachFileIcon />} sx={{ textTransform: "none" }} onClick={() => inputArchivos.current?.click()}>Adjuntar</Button>
                </>
              )}
            >
              {archivos.length === 0 && <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Sin archivos.</Typography>}
              {archivos.map((a) => (
                <Box key={a.archivo} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AttachFileIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                  <Link href={`${ENV.API_BASE_URL}${a.url}`} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12.5, flex: 1, minWidth: 0 }} noWrap>{a.nombre}</Link>
                  {editable && (
                    <Tooltip title="Quitar archivo">
                      <IconButton size="small" aria-label={`Quitar ${a.nombre}`} onClick={() => setConfirmar({ titulo: "Quitar archivo", texto: `¿Quitar «${a.nombre}»?`, accion: () => eliminarArchivoOC(orden.id, a.archivo), exito: "Archivo quitado.", boton: "Sí, quitar", color: "error" })}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Tarjeta>
          </Box>

          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Tarjeta titulo="Resumen de costos">
              <TableContainer>
                <Table size="small" sx={{ "& td, & th": { py: 0.6, px: 1.25, fontSize: 12.5, fontVariantNumeric: "tabular-nums" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell align="right" sx={{ fontWeight: 800, color: "success.dark" }}>Bolivianos</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "info.dark" }}>Dólares</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Total compra</TableCell>
                      <TableCell align="right">{fmt(totales.bolivianos)}</TableCell>
                      <TableCell align="right">{fmt(totales.dolares)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Total gastado</TableCell>
                      <TableCell align="right">{fmt(totales.pagado_bs)}</TableCell>
                      <TableCell align="right">{fmt(totales.pagado_usd)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Crédito fiscal</TableCell>
                      <TableCell align="right">{fmt(totales.credito_fiscal_bs)}</TableCell>
                      <TableCell align="right">{fmt(totales.credito_fiscal_usd)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <Tooltip title="(Total gastado − Crédito fiscal) ÷ Total compra: cuánto cuesta realmente cada unidad respecto a su precio de compra.">
                        <TableCell sx={{ fontWeight: 800 }}>Factor de costo</TableCell>
                      </Tooltip>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{totales.factor === null ? "—" : fmt(totales.factor)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{totales.factor === null ? "—" : fmt(totales.factor)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {totales.factor === null && (
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>El factor se calcula cuando hay pagos emitidos y el total de la compra.</Typography>
              )}
            </Tarjeta>
            <Tarjeta
              titulo="Aduana / Factura"
              acciones={editable && <Button size="small" startIcon={<AddIcon />} onClick={() => setAduana({ abierto: true, aduana: null })} sx={{ textTransform: "none" }}>Agregar aduana</Button>}
            >
              {aduanas.length === 0 && <Alert severity="info" sx={{ py: 0 }}>No hay registro de aduana.</Alert>}
              {aduanas.map((a) => (
                <Box key={a.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                  <Dato etiqueta="Agente" valor={a.agente} />
                  <Dato etiqueta="Factura / Póliza" valor={`${a.num_factura || "—"} / ${a.num_poliza || "—"}`} />
                  <Dato etiqueta="Fecha póliza" valor={a.fecha_poliza} />
                  <Dato etiqueta="Monto" valor={`${simbolo(a.moneda)} ${fmt(a.monto)} (T.C. ${a.tipo_cambio})`} />
                  <Dato etiqueta="Crédito fiscal" valor={`${simbolo(a.moneda)} ${fmt(a.credito_fiscal)}`} />
                  {editable && <Box sx={{ textAlign: "right" }}><Button size="small" startIcon={<EditIcon />} onClick={() => setAduana({ abierto: true, aduana: a })} sx={{ textTransform: "none" }}>Modificar</Button></Box>}
                </Box>
              ))}
            </Tarjeta>

          </Box>
        </Box>

        <Tarjeta
          titulo="Detalles de compra"
          acciones={editable && <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setLinea({ abierto: true, linea: null })} sx={{ textTransform: "none" }}>Agregar detalle</Button>}
        >
          {lineas.length === 0 ? <Alert severity="warning" sx={{ py: 0 }}>Esta orden todavía no tiene detalle de compra.</Alert> : (
            <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    {["Cant.", "Detalle", "Marca", "Servicio", "Tipo de compra", "Proveedor"].map((h) => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
                    <TableCell sx={headSx} align="right">P. unitario</TableCell>
                    <TableCell sx={headSx} align="right">P. total</TableCell>
                    <TableCell sx={headSx} align="right"><Tooltip title="Costo unitario real: precio × (pagado − crédito fiscal) / total de la compra"><span>C. U.</span></Tooltip></TableCell>
                    <TableCell sx={headSx} align="center">Equipos</TableCell>
                    <TableCell sx={headSx} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.map((l) => (
                    <TableRow key={l.id} hover>
                      <TableCell sx={cellSx}>{l.cantidad}</TableCell>
                      <TableCell sx={{ ...cellSx, minWidth: 200 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{l.equipo} {l.detalle}</Typography>
                        {l.observacion && <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{l.observacion}</Typography>}
                      </TableCell>
                      <TableCell sx={cellSx}>{l.marca}</TableCell>
                      <TableCell sx={cellSx}>{l.servicio_nombre}</TableCell>
                      <TableCell sx={cellSx}>{l.tipo_compra}</TableCell>
                      <TableCell sx={cellSx}>{l.proveedor}</TableCell>
                      <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }} align="right">{fmt(l.precio)}</TableCell>
                      <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", fontWeight: 700 }} align="right">{fmt(l.precio_total)}</TableCell>
                      <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }} align="right">{l.costo_unitario === null ? "—" : fmt(l.costo_unitario)}</TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }} align="center">
                        {l.equipos === "no" && (
                          <Tooltip title={l.equipos_motivo || "No se registra por serie"}><Chip size="small" variant="outlined" label="Por cantidad" sx={{ height: 22, fontSize: 11 }} /></Tooltip>
                        )}
                        {l.equipos === "serie" && (
                          <Tooltip title={l.equipos_registrados > 0 ? "Equipos recibidos (la línea ya no se puede modificar ni eliminar)" : "Registrar los equipos recibidos"}>
                            <Button
                              size="small" variant={l.equipos_registrados >= l.cantidad_entera ? "contained" : "outlined"} color={l.equipos_registrados >= l.cantidad_entera ? "success" : "primary"}
                              startIcon={<Inventory2Icon sx={{ fontSize: 16 }} />} onClick={() => setEquiposLinea(l.id)} aria-label={`Equipos de la línea ${l.id}`}
                              sx={{ textTransform: "none", fontWeight: 700, fontSize: 11.5, py: 0, px: 1, minWidth: 0 }}
                            >{l.equipos_registrados}/{l.cantidad_entera}</Button>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap", width: 84 }} align="center">
                        {l.equipos_registrados === 0 && editable && (
                          <>
                            <Tooltip title="Modificar"><IconButton size="small" aria-label={`Modificar línea ${l.id}`} onClick={() => setLinea({ abierto: true, linea: l })}><EditIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" color="error" aria-label={`Eliminar línea ${l.id}`} onClick={() => setConfirmar({ titulo: "Eliminar detalle", texto: `¿Eliminar «${l.equipo} ${l.detalle}» de esta orden?`, accion: () => eliminarLinea(orden.id, l.id), exito: "Detalle eliminado.", boton: "Sí, eliminar", color: "error" })}>
                                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ ...cellSx, fontWeight: 800 }}>Total de la compra</TableCell>
                    <TableCell align="right" sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{sim} {fmt(totales.compra)}</TableCell>
                    <TableCell colSpan={3} sx={{ ...cellSx, color: "text.secondary" }}>Bs {fmt(totales.bolivianos)} · $us {fmt(totales.dolares)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Tarjeta>

        <Tarjeta
          titulo="Detalle de pago"
          acciones={editable && <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setEnlazar(true)} sx={{ textTransform: "none" }}>Agregar pago emitido</Button>}
        >
          {emitidos.length === 0 ? (
            <Alert severity="info" sx={{ py: 0 }}>
              Todavía no hay pagos emitidos. Para pedir un pago, usa <Link component={RouterLink} to="/solicitud-cheque">Solicitud de cheque</Link> con la opción «Orden de compra»; cuando se emita el cheque aparecerá aquí.
            </Alert>
          ) : (
            <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    {["Egreso", "Dirigido a", "N.º factura", "Tipo", "Forma de pago", "Monto factura", "Envío"].map((h) => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
                    <TableCell sx={headSx} align="right">Egreso Bs</TableCell>
                    <TableCell sx={headSx} align="right">Egreso $us</TableCell>
                    <TableCell sx={headSx} align="center">Ver</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emitidos.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{p.id_egreso}</TableCell>
                      <TableCell sx={cellSx}>{p.egreso?.nombre}</TableCell>
                      <TableCell sx={cellSx}>{p.num_factura}</TableCell>
                      <TableCell sx={cellSx}>{p.tipo}</TableCell>
                      <TableCell sx={cellSx}>{p.forma_pago}</TableCell>
                      <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}>{p.monto ? `${p.moneda === "Dolares" ? "$us" : "Bs"} ${fmt(p.monto)}` : "—"}</TableCell>
                      <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}>{p.costo_envio ? fmt(p.costo_envio) : "—"}</TableCell>
                      <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }} align="right">{fmt(p.egreso?.bolivianos)}</TableCell>
                      <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }} align="right">{fmt(p.egreso?.dolares)}</TableCell>
                      <TableCell sx={cellSx} align="center">
                        {puedeVerEgreso && <Tooltip title="Ver comprobante del egreso"><IconButton size="small" aria-label={`Ver egreso ${p.id_egreso}`} onClick={() => setEgresoVer(p.id_egreso)}><VisibilityIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ ...cellSx, fontWeight: 800 }}>Total pagado</TableCell>
                    <TableCell align="right" sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmt(totales.pagado_bs)}</TableCell>
                    <TableCell align="right" sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmt(totales.pagado_usd)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {pendientes.length > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {pendientes.length} pago(s) solicitado(s) todavía sin cheque emitido: solicitud{pendientes.length === 1 ? "" : "es"} {pendientes.map((p) => p.id_solicitud).filter(Boolean).join(", ")}.
            </Alert>
          )}
          {anulados.length > 0 && (
            <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
              <b>Solicitud anulada:</b> {anulados.length} pago(s) se pidió pero su solicitud de cheque fue anulada (solicitud{anulados.length === 1 ? "" : "es"} {anulados.map((p) => p.id_solicitud).filter(Boolean).join(", ")}).
              Ya no están pendientes: si hace falta pagarlos, hay que hacer una solicitud nueva.
            </Alert>
          )}
          {emitidos.length > 0 && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Crédito fiscal descontado: Bs {fmt(totales.credito_fiscal_bs)} · $us {fmt(totales.credito_fiscal_usd)}
              {totales.factor !== null && ` · Factor de costo: ${totales.factor}`}
            </Typography>
          )}
        </Tarjeta>
      </Box>

      <OrdenFormDialog open={editar} orden={orden} catalogos={catalogos} onClose={() => setEditar(false)} onGuardada={() => { setEditar(false); ok("Orden actualizada."); cargar(); }} />
      <LineaFormDialog
        open={linea.abierto} ordenId={orden.id} moneda={orden.moneda} linea={linea.linea} catalogos={catalogos} proveedorOrden={orden.proveedor}
        onClose={() => setLinea({ abierto: false, linea: null })} onGuardada={() => { setLinea({ abierto: false, linea: null }); ok("Detalle guardado."); cargar(); }}
      />
      <AduanaFormDialog
        open={aduana.abierto} ordenId={orden.id} aduana={aduana.aduana} cambioOrden={orden.tipo_cambio}
        onClose={() => setAduana({ abierto: false, aduana: null })} onGuardada={() => { setAduana({ abierto: false, aduana: null }); ok("Aduana guardada."); cargar(); }}
      />
      <EquiposLineaDialog ordenId={orden.id} lineaId={equiposLinea} onClose={() => setEquiposLinea(null)} onCambio={cargar} />
      <EnlazarPagoDialog open={enlazar} ordenId={orden.id} catalogos={catalogos} totales={totales} onClose={() => setEnlazar(false)} onGuardado={() => { setEnlazar(false); ok("Pago enlazado."); cargar(); }} />
      {puedeVerEgreso && <ComprobanteDialog id={egresoVer} onClose={() => setEgresoVer(null)} onCambio={() => {}} />}

      <Dialog open={!!confirmar} onClose={procesando ? undefined : () => setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 17, fontWeight: 800 }}>{confirmar?.titulo}</DialogTitle>
        <DialogContent><DialogContentText>{confirmar?.texto}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)} disabled={procesando} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" color={confirmar?.color || "primary"} onClick={ejecutar} disabled={procesando} sx={{ textTransform: "none", fontWeight: 700 }}>
            {procesando ? <CircularProgress size={20} color="inherit" /> : confirmar?.boton}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!aviso.texto} autoHideDuration={4500} onClose={() => setAviso((a) => ({ ...a, texto: "" }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={aviso.severidad} variant="filled" onClose={() => setAviso((a) => ({ ...a, texto: "" }))}>{aviso.texto}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
